import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://lawn-app.supabasekong.bsidesolutions.net';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJzdXBhYmFzZSIsImlhdCI6MTc1NzYxMzI0MCwiZXhwIjo0OTEzMjg2ODQwLCJyb2xlIjoiYW5vbiJ9.EAOfCPBe37xpp96wCqGvrJWE4RxB9xn6TSx9lZgUleo';

// API base URL - defaults to same origin in production
const apiBaseUrl = import.meta.env.VITE_API_URL || '';

// Store token in localStorage
const getToken = () => localStorage.getItem('lawnapp_token');
const setToken = (token: string) => localStorage.setItem('lawnapp_token', token);
const removeToken = () => localStorage.removeItem('lawnapp_token');

// Auth API functions
export const authApi = {
  async register(email: string, password: string, fullName: string) {
    const response = await fetch(`${apiBaseUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, fullName }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }
    
    const data = await response.json();
    setToken(data.token);
    return data;
  },

  async login(email: string, password: string) {
    const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }
    
    const data = await response.json();
    setToken(data.token);
    return data;
  },

  async getCurrentUser() {
    const token = getToken();
    if (!token) return null;
    
    const response = await fetch(`${apiBaseUrl}/api/auth/me`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      removeToken();
      return null;
    }
    
    return response.json();
  },

  async updateProfile(updates: { fullName?: string; avatarUrl?: string; selectedCity?: string }) {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    
    const response = await fetch(`${apiBaseUrl}/api/auth/profile`, {
      method: 'PATCH',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Update failed');
    }
    
    return response.json();
  },

  logout() {
    removeToken();
  },
};

// Favorites API functions
export const favoritesApi = {
  async getFavorites(userId: string) {
    const response = await fetch(`${apiBaseUrl}/api/favorites?userId=${userId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch favorites');
    }
    
    return response.json();
  },

  async getFavoriteIds(userId: string) {
    const response = await fetch(`${apiBaseUrl}/api/favorites/ids?userId=${userId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch favorite IDs');
    }
    
    return response.json();
  },

  async addFavorite(userId: string, saleId: string) {
    const response = await fetch(`${apiBaseUrl}/api/favorites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, saleId }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add favorite');
    }
    
    return response.json();
  },

  async removeFavorite(userId: string, saleId: string) {
    const response = await fetch(`${apiBaseUrl}/api/favorites/${saleId}?userId=${userId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error('Failed to remove favorite');
    }
    
    return response.json();
  },

  async syncFavorites(userId: string, saleIds: string[]) {
    const response = await fetch(`${apiBaseUrl}/api/favorites/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, saleIds }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to sync favorites');
    }
    
    return response.json();
  },
};

// Regular Supabase client for sales data (direct Supabase is fine)
export const supabase = createClient(supabaseUrl, supabaseAnonKey);